import React from 'react';
import { KatDivider, KatIcon } from '@amzn/katal-react';
import { LanguageSelector, ModuleDescription, SuButton } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import HMDFeedback from 'src/components/hmd-feedback';
import { t } from 'src/utils/translation';
import { InstanceLanguage, LANGUAGE_STRING_ID_MAP } from 'src/constants/languageSelector';
import { BUNDLE_NAME, LEARNING_MODULE_STRING_ID } from 'src/constants/localization';
import { useMetrics } from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { useParentEntityContext } from 'src/contexts/ParentEntityContext';
import { useClientId } from 'src/contexts/ClientContext';
import { getModuleCtaRefTag, getModuleDownloadRefTag } from 'src/constants/refTag';
import useDocumentCompletionProgress from 'src/hooks/useDocumentCompletionProgress';
import type { LearningModuleViewProps } from '../LearningModuleContentView';

export function DocumentView({
  learningModule, moduleInstance, handleLanguageSelect,
  unauthenticated, clientId: propClientId,
}: LearningModuleViewProps) {
  const [learningModuleBundle] = useBundle(BUNDLE_NAME.LEARNING_MODULE);
  const { instanceMap } = learningModule;
  const { parentId, parentType } = useParentEntityContext();
  const contextClientId = useClientId();

  // Use provided clientId if available, otherwise use the one from context
  const clientId = propClientId ?? contextClientId;

  const languageOptions = Object.values(instanceMap).map(({ instanceLanguage }) => ({
    value: instanceLanguage,
    name: LANGUAGE_STRING_ID_MAP[instanceLanguage as InstanceLanguage], // will make a follow-up CR to fix typing on the data model itself
  }));

  const selectedPdfUrl = moduleInstance.urlMap.PDF;

  useDocumentCompletionProgress(moduleInstance, propClientId);

  const { clickMetric } = useMetrics();

  const ctaRefTag = getModuleCtaRefTag(parentType);
  const downloadRefTag = getModuleDownloadRefTag(parentType);

  const handleModuleCtaClick = () => {
    clickMetric({
      feature: FeatureName.ModuleCta,
      metadata: {
        moduleId: learningModule.moduleId,
        moduleTitle: learningModule.title,
        ctaLink: learningModule.ctaLink,
        refTag: downloadRefTag,
        url: window.location.href,
        parentType,
        parentId,
        clientId,
      },
    });
  };

  const handleMoreInfoLinkClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    const href = anchor?.getAttribute('href');
    if (anchor && href && learningModule.subContentList?.some((item) => item.link === href)) {
      /* istanbul ignore next -- defensive fallback for null textContent */
      const linkTitle = anchor.textContent ?? '';
      clickMetric({
        feature: FeatureName.ModuleMoreInfoLink,
        metadata: {
          moduleId: learningModule.moduleId,
          linkUrl: href,
          linkTitle,
          url: window.location.href,
        },
      });
    }
  };

  const handlePdfDownloadClick = () => {
    clickMetric({
      feature: FeatureName.ModuleDownloadBtn,
      metadata: {
        moduleId: learningModule.moduleId,
        moduleTitle: learningModule.title,
        pdfUrl: selectedPdfUrl,
        refTag: downloadRefTag,
        url: window.location.href,
        parentType,
        parentId,
        clientId,
      },
    });
  };

  return (
    <div className="flex w-full flex-col gap-su-space-05 sm_kat_modal:w-[3/4] md:flex-row">
      <div className="size-full bg-su-border-divider-default p-su-space-05 md:w-[60vw] md:max-w-screen-sm_kat_modal">
        <object
          className="h-[60vh] w-full min-w-full md:h-[75vh]"
          type="application/pdf"
          id="doc-preview-embed"
          data={selectedPdfUrl}
          aria-label="PDF document viewer"
        />
      </div>
      <div className="m-su-space-05 flex max-w-full flex-col items-end gap-su-space-05 md:w-2/5 md:min-w-[15rem]">
        <ModuleDescription
          className="border-hidden p-su-space-00"
          id={learningModule.moduleId}
          content={learningModule.description}
          title={t(learningModuleBundle, LEARNING_MODULE_STRING_ID.MODULE_DESCRIPTION_SUMMARY_TEXT)}
          {...(learningModule.subContentList?.length && !unauthenticated && {
            moreInformationTitle: t(
              learningModuleBundle,
              LEARNING_MODULE_STRING_ID.MORE_INFORMATION_TITLE,
            ),
          })}
          moreInformationItems={
            !unauthenticated ? learningModule.subContentList?.map((subContent) => ({
              link: subContent.link,
              title: subContent.title,
            })) : undefined
          }
          onClick={handleMoreInfoLinkClick}
        />
        <KatDivider className="w-full" variant="athens" />
        <LanguageSelector
          contentType={t(learningModuleBundle, LEARNING_MODULE_STRING_ID.PDF_LANGUAGE_SELECTOR_TEXT)}
          languages={languageOptions}
          selectedLanguage={moduleInstance.instanceLanguage}
          onLanguageChange={handleLanguageSelect}
        />
        <div className="my-su-space-05 flex flex-col items-end gap-su-space-05">
          {!unauthenticated && (
            <SuButton
              variant="primary"
              label={learningModule.ctaText}
              linkProps={{
                href: learningModule.ctaLink,
                refTag: ctaRefTag,
                onClick: handleModuleCtaClick,
              }}
            />
          )}
          <SuButton
            variant="secondary"
            label={t(learningModuleBundle, LEARNING_MODULE_STRING_ID.DOWNLOAD_PDF_TEXT)}
            linkProps={{
              href: selectedPdfUrl,
              download: selectedPdfUrl,
              refTag: downloadRefTag,
              onClick: handlePdfDownloadClick,
            }}
          >
            <KatIcon name="file_download" size="small" slot="icon" />
          </SuButton>
        </div>
        {!unauthenticated && <HMDFeedback instance={moduleInstance} />}
      </div>
    </div>
  );
}

export default DocumentView;
